/**
 * Grid-view entry to the brainstorm (the script-view entry is ScriptView's
 * highlight menu): select any cells — multiple rows, multiple columns — and
 * the F-27 selection toolbar grows a sparkle. Click → the same conversation
 * panel, centered as a dialog.
 *
 * WHY A DIALOG, when the script view anchors a popover under the highlight.
 * The F-27 toolbar's popover slot mousedown-prevents everything but inputs to
 * keep grid focus (SelectionToolbar's contract), and the pill closes its
 * popover whenever the selection or camera changes — both correct for a
 * palette, both fatal for a conversation the writer types into and reads
 * across minutes of panning. The dialog opts out of that lifecycle: it holds a
 * SNAPSHOT of the selection taken at click time, so panning, or even losing
 * the grid selection entirely, does not close it mid-thought.
 *
 * The registry pattern is translate's, exactly: `run(ctx)` receives only a
 * session, so the open-dialog state is a module singleton the layer
 * subscribes to (see translate/store.ts's note on why there is no prop path).
 *
 * Context shape: the highlighted cells become the passage (`gridSelectionLines`
 * — column-labelled once the selection spans columns) and the Original Script
 * column rides along whole, same as the script view. Asking about a
 * TRANSLATION cell with the source script in view is exactly the point.
 */
import { useCallback, useEffect, useMemo } from "react";
import { createStore, type StoreApi } from "zustand/vanilla";
import { useStore } from "zustand";

import type { BoardSession } from "../../core/session.js";
import {
  registerToolbarAction,
  type ToolbarActionContext,
} from "../../registries/toolbar-actions/index.js";
import { buildBrainstormContext, capSelection } from "./brainstorm.js";
import { BrainstormPopover, SparkleIcon } from "./BrainstormPopover.js";
import { gridSelectionHasText, gridSelectionLines, type GridSelectionInput } from "./grid-lines.js";

/** The cells a dialog is open for — a SNAPSHOT of the selection at click. */
export interface GridBrainstormTarget {
  boardId: string;
  sheetId: string;
  rowIds: readonly string[];
  colIds: readonly string[];
}

interface GridBrainstormState {
  target: GridBrainstormTarget | null;
  open(target: GridBrainstormTarget): void;
  close(): void;
}

/** Module singleton, board-scoped — the layer ignores other boards' targets. */
export const gridBrainstormUi: StoreApi<GridBrainstormState> = createStore<GridBrainstormState>(
  (set) => ({
    target: null,
    open: (target) => set({ target }),
    close: () => set({ target: null }),
  }),
);

/** Bind the session's doc reads for the pure helpers. */
function selectionInput(
  session: BoardSession,
  target: GridBrainstormTarget,
): GridSelectionInput {
  return {
    rowIds: target.rowIds,
    colIds: target.colIds,
    textOf: (rowId, colId) => session.board.getCellText(target.sheetId, rowId, colId),
    colName: (colId) => session.board.getColumn(target.sheetId, colId)?.name ?? "",
  };
}

/**
 * The whole Original Script column, for the script-context half. Walked at
 * ask time, not render time — same cost reasoning as ScriptView's
 * `readBrainstormContext` (a 50k-row walk must not run per keystroke).
 */
function readGridBrainstormContext(session: BoardSession, target: GridBrainstormTarget) {
  const sourceColId = session.board.sourceColumnId(target.sheetId);
  const script: string[] = [];
  if (sourceColId !== undefined) {
    for (const rowId of session.board.sortedRowIds(target.sheetId)) {
      script.push(session.board.getCellText(target.sheetId, rowId, sourceColId));
    }
  }
  return buildBrainstormContext(script, gridSelectionLines(selectionInput(session, target)));
}

/* ------------------------------------------------------------------ */
/* The dialog layer — BoardCanvas mounts one per board                 */
/* ------------------------------------------------------------------ */

export function BrainstormGridLayer({ session }: { session: BoardSession }) {
  const target = useStore(gridBrainstormUi, (s) => s.target);
  const close = useCallback(() => gridBrainstormUi.getState().close(), []);

  const mine = target !== null && target.boardId === session.boardId ? target : null;

  // The quote header shows the cells as they stood when the dialog OPENED —
  // a stable statement of what was asked about. The request itself re-reads
  // them at ask time (readContext), the same split the script view makes.
  const opened = useMemo(
    () => (mine === null ? null : capSelection(gridSelectionLines(selectionInput(session, mine)))),
    [session, mine],
  );

  const readContext = useCallback(
    () => (mine === null ? EMPTY_CONTEXT : readGridBrainstormContext(session, mine)),
    [session, mine],
  );

  // Esc closes the DIALOG, capture-phase so the grid's own Esc handler
  // (clear selection) never sees the key while the dialog is up. The panel's
  // textarea also maps Esc→close, but only while focused — this covers a
  // writer who clicked away to read the answer.
  const isOpen = mine !== null;
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      close();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [isOpen, close]);

  if (mine === null || opened === null) return null;

  return (
    <div
      className="brainstorm-modal-backdrop"
      onMouseDown={(event) => {
        // Backdrop only — a click inside the panel must not close it.
        if (event.target === event.currentTarget) close();
      }}
    >
      <BrainstormPopover
        boardId={mine.boardId}
        selection={opened.selection}
        selectionDropped={opened.dropped}
        readContext={readContext}
        onClose={close}
      />
    </div>
  );
}

const EMPTY_CONTEXT = {
  script: [],
  sampled: false,
  totalLines: 0,
  selection: [],
  selectionDropped: 0,
};

/* ------------------------------------------------------------------ */
/* F-27 toolbar action                                                 */
/* ------------------------------------------------------------------ */

const iconAskAi = <SparkleIcon />;

function hasAskableText(ctx: ToolbarActionContext): boolean {
  const sel = ctx.selection;
  if (sel.kind !== "cells") return false;
  return gridSelectionHasText(
    selectionInput(ctx.session, {
      boardId: ctx.session.boardId,
      sheetId: sel.sheetId,
      rowIds: sel.rowIds,
      colIds: sel.colIds,
    }),
  );
}

registerToolbarAction({
  id: "script-brainstorm",
  appliesTo: "cells",
  tooltip: "Ask AI about the selected cells",
  // Between translate (20) and design-rows (30): questions about the words
  // sit next to the actions that transform them.
  order: 25,
  // DISABLED (not hidden) on an all-blank selection: unlike translate's
  // one-column rule, any selection COULD be asked about once it has text, so
  // the greyed sparkle correctly says "select something with words in it".
  enabled: hasAskableText,
  icon: () => iconAskAi,
  run: (ctx) => {
    const sel = ctx.selection;
    if (sel.kind !== "cells") return;
    gridBrainstormUi.getState().open({
      boardId: ctx.session.boardId,
      sheetId: sel.sheetId,
      // Copied, not aliased — the toolbar's arrays are re-published on every
      // selection change and the snapshot must not drift with them.
      rowIds: [...sel.rowIds],
      colIds: [...sel.colIds],
    });
  },
});
